/**
 * Hotel voucher body — Phase 9 rebuild (target: 1 page, max 2 pages).
 *
 * Sections (all compact, 12-13px body, 12px padding, 12px gap):
 *   1.  Hotel header     — 60/40 split: name + stars/property badge +
 *                          address + contact line  |  confirmation box
 *   2.  Stay grid        — 6 cells: check-in / check-out / nights /
 *                          room type / rooms / meal plan
 *   3.  Guests           — lead-guest + all-guests boxes, pax summary line
 *   4.  Extra bed        — conditional, amber inline card
 *   5.  Transfers        — conditional
 *   6.  Inclusions       — conditional
 *   7.  Amenities        — conditional inline badges
 *   8.  Cancellation     — conditional, gated by showCancellation prop
 *   9.  Check-in/out time note
 *   10. Important notes  — default template applied if value is empty/missing
 *   11. NO barcode       — hotel vouchers never render VoucherBarcodeStrip
 *
 * Inline edit pattern: same as ActivityVoucherBody — InlineEditableField for
 * single values, EditableArraySectionCard for array sections. Edit pencils
 * only render when `editable && !locked`. Status === 'issued' → locked.
 */
import React, { useState, useEffect } from 'react';
import { Pencil } from 'lucide-react';
import { InlineEditableField } from './VoucherEditComponents';
import { formatVoucherDate } from '../../lib/voucherDate';

// ------------- helpers -------------
const MEAL_PLAN_LABELS = {
  BB: 'Bed & Breakfast',
  HB: 'Half Board',
  MAP: 'Modified American Plan (Breakfast + Dinner)',
  FB: 'Full Board',
  RO: 'Room Only',
  AI: 'All Inclusive (meals, drinks, selected activities)',
  UAI: 'Ultra All Inclusive',
  'PREMIUM AI': 'Premium All Inclusive',
  CP: 'Continental Plan',
  EP: 'European Plan (Room Only)',
};

const expandMealPlan = (mp) => {
  if (!mp) return '';
  const key = String(mp).trim().toUpperCase();
  return MEAL_PLAN_LABELS[key] || mp;
};

export const DEFAULT_HOTEL_IMPORTANT_NOTES = [
  'City tax, tips and gratuities are not included and payable directly at the hotel.',
  'Early check-in and late check-out are subject to availability and may attract additional charges.',
  'Bed type (twin or double) is allocated by the hotel at check-in based on availability.',
  "Room upgrades are subject to availability at the hotel's discretion.",
];

const renderStars = (count, primary) => {
  const n = Math.max(0, Math.min(5, parseInt(count, 10) || 0));
  return (
    <span style={{ fontSize: '15px', letterSpacing: '1px' }}>
      <span style={{ color: '#F59E0B' }}>{'★'.repeat(n)}</span>
      <span style={{ color: '#D1D5DB' }}>{'☆'.repeat(5 - n)}</span>
    </span>
  );
};

const SectionCaption = ({ children, color = '#6b7280' }) => (
  <div
    style={{
      fontSize: '10px',
      fontWeight: 600,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color,
      marginBottom: '4px',
    }}
  >
    {children}
  </div>
);

const CompactCard = ({ children, style = {} }) => (
  <div
    style={{
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      padding: '10px 12px',
      background: '#ffffff',
      ...style,
    }}
  >
    {children}
  </div>
);

/**
 * CompactEditableArray — compact list (no SectionCard chrome) that exposes an
 * inline pencil to switch into a "one item per line" textarea editor. Used
 * for every hotel array field (allGuests, transferDetails, inclusions,
 * amenities, cancellationPolicy, importantNotes). Saves via onSave(arr).
 */
function CompactEditableArray({
  items,
  editable,
  locked,
  fieldKey,
  onFieldSave,
  caption,
  captionColor,
  renderItem,
  emptyHint = 'Click the pencil to add',
  testId,
  defaultTemplate = null,        // optional string[] used to seed the
                                 // textarea on first edit when items is empty
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const arr = Array.isArray(items) ? items : [];

  useEffect(() => {
    if (!isEditing) setDraft(arr.join('\n'));
  }, [arr, isEditing]);

  const startEdit = () => {
    const seed = arr.length === 0 && Array.isArray(defaultTemplate)
      ? defaultTemplate.join('\n')
      : arr.join('\n');
    setDraft(seed);
    setIsEditing(true);
  };

  const save = async () => {
    const lines = String(draft || '')
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    setSaving(true);
    try {
      await onFieldSave?.(fieldKey, lines);
      setIsEditing(false);
    } catch (e) {
      console.error('hotel array save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const pencilBtn = editable && !locked && !isEditing && (
    <button
      type="button"
      onClick={startEdit}
      data-testid={`${testId}-edit-btn`}
      aria-label={`Edit ${caption}`}
      style={{
        background: 'transparent',
        border: '1px solid #e5e7eb',
        borderRadius: '4px',
        padding: '1px 5px',
        cursor: 'pointer',
        color: '#9ca3af',
        lineHeight: 1,
        marginLeft: '6px',
      }}
    >
      <Pencil size={10} />
    </button>
  );

  // Hide entirely when not editable AND empty — keeps PDF clean.
  if (!editable && arr.length === 0) return null;

  return (
    <div data-testid={testId}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
        <SectionCaption color={captionColor || '#6b7280'}>{caption}</SectionCaption>
        {pencilBtn}
      </div>
      {isEditing ? (
        <div data-testid={`${testId}-editor`}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(3, arr.length + 1)}
            autoFocus
            data-testid={`${testId}-textarea`}
            style={{
              width: '100%',
              resize: 'vertical',
              border: '2px solid #0891b2',
              borderRadius: '4px',
              padding: '6px 8px',
              outline: 'none',
              background: '#f0f9ff',
              fontSize: '13px',
              lineHeight: 1.45,
              fontFamily: 'Inter, sans-serif',
            }}
            placeholder="One item per line"
          />
          <div style={{ marginTop: '6px' }}>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid={`${testId}-save`}
              style={{
                padding: '4px 12px',
                background: '#0891b2',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setIsEditing(false); setDraft(arr.join('\n')); }}
              data-testid={`${testId}-cancel`}
              style={{
                marginLeft: '6px',
                padding: '4px 10px',
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : arr.length === 0 ? (
        <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#9ca3af' }}>
          — {emptyHint}
        </div>
      ) : (
        renderItem(arr)
      )}
    </div>
  );
}

// ------------- main body -------------

/**
 * HotelContactEditor — inline phone/email/website editor for the hotel header.
 * Three fields in one block. Amber empty-state when all are blank.
 * Saves via onFieldSave (the existing PATCH path) — one PATCH per
 * changed field, sequentially. Per Standing Instruction #5 there's no
 * speculative bulk-save endpoint added.
 */
function HotelContactEditor({ phone, email, website, editable, locked, onFieldSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ phone: phone || '', email: email || '', website: website || '' });
  const [saving, setSaving] = useState(false);

  // Re-sync draft whenever the saved props change (after PATCH or initial load).
  useEffect(() => {
    if (!isEditing) setDraft({ phone: phone || '', email: email || '', website: website || '' });
  }, [phone, email, website, isEditing]);

  const isEmpty = !phone && !email && !website;
  const fieldKeyMap = { phone: 'hotelPhone', email: 'hotelEmail', website: 'hotelWebsite' };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changes = [];
      if ((draft.phone || '') !== (phone || '')) changes.push(['phone', draft.phone]);
      if ((draft.email || '') !== (email || '')) changes.push(['email', draft.email]);
      if ((draft.website || '') !== (website || '')) changes.push(['website', draft.website]);
      for (const [field, val] of changes) {
        await onFieldSave?.(fieldKeyMap[field], val);
      }
      setIsEditing(false);
    } catch (e) {
      console.error('hotel contact save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  // ----- display mode -----
  if (!isEditing) {
    const display = [phone, email, website].filter(Boolean).join(' · ');
    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}
        data-testid="hotel-contact-line"
      >
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          {display || (editable ? (
            <span style={{ color: '#f59e0b', fontStyle: 'italic' }} data-testid="hotel-contact-empty">
              No contact details — tap ✎ to add
            </span>
          ) : '')}
        </span>
        {editable && !locked && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            data-testid="hotel-contact-edit-btn"
            aria-label="Edit hotel contact details"
            style={{
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              padding: '2px 6px',
              cursor: 'pointer',
              color: isEmpty ? '#f59e0b' : '#9ca3af',
              fontSize: '12px',
              lineHeight: 1,
            }}
          >
            ✎
          </button>
        )}
      </div>
    );
  }

  // ----- edit mode -----
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}
      data-testid="hotel-contact-editor"
    >
      {[
        ['phone',   'Phone',   '+91 22 1234 5678'],
        ['email',   'Email',   'hotel@example.com'],
        ['website', 'Website', 'www.hotel.com'],
      ].map(([field, label, ph]) => (
        <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '11px', color: '#9ca3af', minWidth: '60px' }}>{label}</span>
          <input
            type="text"
            value={draft[field]}
            placeholder={ph}
            onChange={(e) => setDraft((p) => ({ ...p, [field]: e.target.value }))}
            data-testid={`hotel-contact-input-${field}`}
            style={{
              flex: 1,
              fontSize: '12px',
              border: '1px solid #0891b2',
              borderRadius: '4px',
              padding: '5px 8px',
              outline: 'none',
              background: '#f0f9ff',
              fontFamily: 'Inter, sans-serif',
            }}
          />
        </div>
      ))}
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button
          type="button"
          disabled={saving}
          onClick={handleSave}
          data-testid="hotel-contact-save-btn"
          style={{
            padding: '5px 16px',
            background: '#0891b2',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => { setDraft({ phone: phone || '', email: email || '', website: website || '' }); setIsEditing(false); }}
          data-testid="hotel-contact-cancel-btn"
          style={{
            padding: '5px 12px',
            background: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '12px',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


export default function HotelVoucherBody({
  voucher,
  company,
  editable = false,
  locked = false,
  onFieldSave,
  showCancellation = true,
}) {
  const h = voucher?.hotel || {};
  const primary = company?.primaryColor || '#34D0CB';

  // Schema-tolerant reads (support both legacy fields and new ones)
  const hotelName = h.hotelName || h.name || '—';
  const checkIn = h.checkIn || h.checkInDate || '';
  const checkOut = h.checkOut || h.checkOutDate || '';
  const checkInTime = h.checkInTime || '';
  const checkOutTime = h.checkOutTime || '';
  const starRating = Number(h.starRating || h.stars || 0);
  const propertyType = (h.propertyType || '').trim();
  const confirmationNumber = (h.confirmationNumber || '').trim();
  const bookingRef = (h.bookingRef || '').trim();
  const bookingRefNote = (h.bookingRefNote || '').trim();
  const address = h.address || '';
  const city = h.city || '';
  const country = h.country || '';
  const postalCode = h.postalCode || '';
  const phone = h.phone || h.contactPhone || '';
  const email = h.email || '';
  const website = h.website || '';
  const roomType = h.roomType || '';
  const rooms = h.rooms || 1;
  const mealPlan = h.mealPlan || '';
  const adults = Number(h.adults || 0);
  const children = Number(h.children || 0);
  const leadGuestName = h.leadGuestName || voucher?.guestName || '';
  const allGuests = Array.isArray(h.allGuests) ? h.allGuests : [];
  const extraBed = h.extraBed === true;
  const extraBedConfirmed = h.extraBedConfirmed === true;
  const extraBedPayableAtHotel = h.extraBedPayableAtHotel === true;
  const transferDetails = Array.isArray(h.transferDetails) ? h.transferDetails : [];
  const inclusions = Array.isArray(h.inclusions) ? h.inclusions : [];
  const amenities = Array.isArray(h.amenities) ? h.amenities : [];
  const cancellationPolicy = Array.isArray(h.cancellationPolicy) ? h.cancellationPolicy : [];
  const rawNotes = Array.isArray(h.importantNotes) ? h.importantNotes : [];
  const importantNotes = rawNotes.length > 0 ? rawNotes : DEFAULT_HOTEL_IMPORTANT_NOTES;

  let nights = h.nights;
  if (!nights && checkIn && checkOut) {
    try {
      const diff = Math.round(
        (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)
      );
      if (Number.isFinite(diff) && diff > 0) nights = diff;
    } catch { /* ignore */ }
  }
  nights = nights || '—';

  const fullAddressLine = [address, city, country, postalCode]
    .filter(Boolean)
    .join(', ');
  const contactLine = [phone, email, website].filter(Boolean).join(' · ');

  const totalGuests = adults + children;
  const showExtraBed = extraBed === true || (totalGuests > 2 && (rooms || 1) === 1);
  const paxSummary = [
    adults ? `${adults} ${adults === 1 ? 'Adult' : 'Adults'}` : '',
    children ? `${children} ${children === 1 ? 'Child' : 'Children'}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const useTwoColInclusions = inclusions.length > 4; // eslint-disable-line no-unused-vars

  return (
    <div style={{ fontSize: '13px', color: '#111827', lineHeight: 1.5 }}>
      {/* Late-confirmation banner — only when missing */}
      {!confirmationNumber && (
        <div
          data-testid="hotel-pending-confirmation-banner"
          style={{
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            padding: '10px 14px',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#92400e',
            marginBottom: '12px',
          }}
        >
          ⏳ Confirmation number pending. Edit this voucher to add it when received from the hotel.
        </div>
      )}

      {/* ---------- Section 1: Hotel Header ---------- */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px' }}>
        <tbody>
          <tr>
            {/* LEFT 60% */}
            <td style={{ verticalAlign: 'top', width: '60%', paddingRight: '12px' }}>
              <div
                style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '20px',
                  fontWeight: 700,
                  lineHeight: 1.15,
                  color: '#111827',
                  marginBottom: '4px',
                }}
                data-testid="hotel-name"
              >
                <InlineEditableField
                  value={hotelName}
                  editable={editable}
                  locked={locked}
                  onSave={(v) => onFieldSave?.('hotelName', v)}
                  displayStyle={{ fontSize: '20px', fontWeight: 700, lineHeight: 1.15 }}
                  ariaLabel="Edit hotel name"
                  testIdPrefix="hotel-name-edit"
                />
              </div>

              {/* Stars OR property badge — inline editable when in edit mode.
                  Rules per spec:
                    starRating  1-5  → renderStars  (gold filled + empty)
                    starRating  0    → if propertyType set, show text badge
                    both empty + editable → show "+ Set rating" placeholder
                    both empty + !editable → hide row entirely */}
              {(starRating > 0 || propertyType || editable) && (
                <div
                  style={{ marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}
                  data-testid="hotel-rating-row"
                >
                  {starRating > 0 && renderStars(starRating, primary)}
                  {starRating === 0 && propertyType && (
                    <span
                      data-testid="hotel-property-type-badge"
                      style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        fontSize: '11px',
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        color: '#0f766e',
                        background: '#ccfbf1',
                        border: '1px solid #5eead4',
                        borderRadius: '12px',
                      }}
                    >
                      {propertyType}
                    </span>
                  )}
                  {starRating === 0 && !propertyType && !editable && null}
                  {editable && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#6b7280' }}>
                      <span data-testid="hotel-star-rating-edit-wrap">
                        <InlineEditableField
                          value={String(starRating || '')}
                          editable={editable}
                          locked={locked}
                          onSave={(v) => {
                            const n = Math.max(0, Math.min(5, parseInt(v, 10) || 0));
                            onFieldSave?.('starRating', n);
                          }}
                          formatValue={(v) => {
                            const n = parseInt(v, 10) || 0;
                            return n > 0 ? `${n}★` : '+ Set stars';
                          }}
                          inputStyle={{ width: '60px' }}
                          ariaLabel="Edit star rating (0-5)"
                          testIdPrefix="hotel-star-rating-edit"
                        />
                      </span>
                      <span data-testid="hotel-property-type-edit-wrap">
                        <InlineEditableField
                          value={propertyType}
                          editable={editable}
                          locked={locked}
                          onSave={(v) => onFieldSave?.('propertyType', v)}
                          formatValue={(v) => v || '+ Set property type'}
                          ariaLabel="Edit property type (Boutique, Heritage, etc.)"
                          testIdPrefix="hotel-property-type-edit"
                        />
                      </span>
                    </span>
                  )}
                </div>
              )}

              {/* Address — editable as a multi-line textarea (up to 4 lines).
                  Newlines are preserved in display so the agent can format
                  long addresses compactly. */}
              {(address || editable) && (
                <div
                  style={{ fontSize: '12px', color: '#4b5563', marginBottom: '2px', whiteSpace: 'pre-line' }}
                  data-testid="hotel-address-line"
                >
                  <InlineEditableField
                    value={address}
                    editable={editable}
                    locked={locked}
                    onSave={(v) => onFieldSave?.('hotelAddress', v)}
                    formatValue={(v) => {
                      const lines = String(v || '')
                        .split(/\r?\n/)
                        .map((s) => s.trim())
                        .filter(Boolean)
                        .slice(0, 4);
                      return lines.join('\n') || (editable ? '—' : '');
                    }}
                    textareaRows={4}
                    inputStyle={{ minWidth: '320px' }}
                    ariaLabel="Edit address"
                    testIdPrefix="hotel-address-edit"
                  />
                </div>
              )}
              {[city, country, postalCode].filter(Boolean).length > 0 && (
                <div style={{ fontSize: '12px', color: '#4b5563', marginBottom: '2px' }} data-testid="hotel-address-locality">
                  {[city, country, postalCode].filter(Boolean).join(', ')}
                </div>
              )}
              {/* Contact line — phone · email · website.
                  Editable as a 3-field block via the ✎ button. Amber when
                  all three are empty (per Phase 9 final spec). Saves via the
                  same handleSaveHotelField path (one PATCH per field). */}
              <HotelContactEditor
                phone={phone}
                email={email}
                website={website}
                editable={editable}
                locked={locked}
                onFieldSave={onFieldSave}
              />
            </td>

            {/* RIGHT 40% — booking box */}
            <td style={{ verticalAlign: 'top', width: '40%' }}>
              <div
                style={{
                  border: `1px solid ${primary}`,
                  borderRadius: '6px',
                  padding: '8px 12px',
                  background: `${primary}10`,
                }}
              >
                {/* TRV voucher number — hotel-specific prefix (TRVDH / TRVIH /
                    TRVVCH legacy). Shown at the top of the confirmation box so
                    the agent can read off the Travcations reference at a glance. */}
                {voucher?.trvRef && (
                  <div style={{ marginBottom: '6px' }} data-testid="hotel-header-trv-ref">
                    <SectionCaption color={primary}>TRV Voucher No.</SectionCaption>
                    <div
                      style={{
                        fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: '#111827',
                        wordBreak: 'break-all',
                      }}
                    >
                      {voucher.trvRef}
                    </div>
                  </div>
                )}
                <SectionCaption color={primary}>Confirmation No.</SectionCaption>
                <div
                  data-testid="hotel-confirmation-number"
                  style={{
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                    fontSize: '15px',
                    fontWeight: 700,
                    color: confirmationNumber ? '#111827' : '#b45309',
                    marginBottom: '6px',
                    wordBreak: 'break-all',
                  }}
                >
                  <InlineEditableField
                    value={confirmationNumber}
                    editable={editable}
                    locked={locked}
                    onSave={(v) => onFieldSave?.('hotelConfirmationNumber', v)}
                    formatValue={(v) => (v && String(v).trim() ? v : 'Pending — to be updated')}
                    ariaLabel="Edit confirmation number"
                    testIdPrefix="hotel-confnum-edit"
                  />
                </div>
                <SectionCaption color={primary}>Booking Ref</SectionCaption>
                <div
                  data-testid="hotel-booking-ref"
                  style={{ fontSize: '12px', color: '#374151', fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                >
                  <InlineEditableField
                    value={bookingRef}
                    editable={editable}
                    locked={locked}
                    onSave={(v) => onFieldSave?.('bookingRef', v)}
                    formatValue={(v) => (v && String(v).trim() ? v : '—')}
                    ariaLabel="Edit booking ref"
                    testIdPrefix="hotel-bookingref-edit"
                  />
                </div>
                {/* Optional manual note next to booking ref, e.g. "confirmed by rsvn" */}
                {(bookingRefNote || editable) && (
                  <div
                    style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', fontStyle: 'italic' }}
                    data-testid="hotel-booking-ref-note"
                  >
                    <InlineEditableField
                      value={bookingRefNote}
                      editable={editable}
                      locked={locked}
                      onSave={(v) => onFieldSave?.('bookingRefNote', v)}
                      formatValue={(v) => (v ? `(${v})` : editable ? '+ add note' : '')}
                      ariaLabel="Edit booking ref note"
                      testIdPrefix="hotel-bookingref-note-edit"
                    />
                  </div>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* ---------- Section 2: Stay grid (2 rows × 3 cols) ---------- */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '8px',
          marginBottom: '12px',
        }}
      >
        <CompactCard>
          <SectionCaption>Check-in</SectionCaption>
          <div style={{ fontWeight: 600 }}>{formatVoucherDate(checkIn) || '—'}</div>
          <div style={{ fontSize: '11px', color: '#6b7280' }}>
            <InlineEditableField
              value={checkInTime}
              editable={editable}
              locked={locked}
              onSave={(v) => onFieldSave?.('checkInTime', v)}
              formatValue={(v) => v || (editable ? '—' : '')}
              ariaLabel="Edit check-in time"
              testIdPrefix="hotel-checkin-time-edit"
            />
          </div>
        </CompactCard>
        <CompactCard>
          <SectionCaption>Check-out</SectionCaption>
          <div style={{ fontWeight: 600 }}>{formatVoucherDate(checkOut) || '—'}</div>
          <div style={{ fontSize: '11px', color: '#6b7280' }}>
            <InlineEditableField
              value={checkOutTime}
              editable={editable}
              locked={locked}
              onSave={(v) => onFieldSave?.('checkOutTime', v)}
              formatValue={(v) => v || (editable ? '—' : '')}
              ariaLabel="Edit check-out time"
              testIdPrefix="hotel-checkout-time-edit"
            />
          </div>
        </CompactCard>
        <CompactCard>
          <SectionCaption>Nights</SectionCaption>
          <div style={{ fontWeight: 600 }}>{nights}{Number.isFinite(Number(nights)) ? ` ${Number(nights) === 1 ? 'Night' : 'Nights'}` : ''}</div>
        </CompactCard>

        <CompactCard>
          <SectionCaption>Room Type</SectionCaption>
          <div style={{ fontWeight: 600 }}>
            <InlineEditableField
              value={roomType}
              editable={editable}
              locked={locked}
              onSave={(v) => onFieldSave?.('roomType', v)}
              formatValue={(v) => v || '—'}
              ariaLabel="Edit room type"
              testIdPrefix="hotel-roomtype-edit"
            />
          </div>
        </CompactCard>
        <CompactCard>
          <SectionCaption>Rooms</SectionCaption>
          <div style={{ fontWeight: 600 }}>{rooms} {Number(rooms) === 1 ? 'Room' : 'Rooms'}</div>
        </CompactCard>
        <CompactCard>
          <SectionCaption>Meal Plan</SectionCaption>
          <div style={{ fontWeight: 600 }} data-testid="hotel-meal-plan">
            <InlineEditableField
              value={mealPlan}
              editable={editable}
              locked={locked}
              onSave={(v) => onFieldSave?.('mealPlan', v)}
              formatValue={(v) => expandMealPlan(v) || 'Room Only'}
              ariaLabel="Edit meal plan"
              testIdPrefix="hotel-mealplan-edit"
            />
          </div>
        </CompactCard>
      </div>

      {/* ---------- Section 3: Guests ---------- */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '6px' }}>
        <CompactCard>
          <SectionCaption>Lead Guest</SectionCaption>
          <div style={{ fontWeight: 600 }} data-testid="hotel-lead-guest">
            <InlineEditableField
              value={leadGuestName}
              editable={editable}
              locked={locked}
              onSave={(v) => onFieldSave?.('hotelLeadGuestName', v)}
              formatValue={(v) => v || '—'}
              ariaLabel="Edit lead guest"
              testIdPrefix="hotel-leadguest-edit"
            />
          </div>
        </CompactCard>
        <CompactCard>
          <CompactEditableArray
            items={allGuests}
            editable={editable}
            locked={locked}
            fieldKey="allGuests"
            onFieldSave={onFieldSave}
            caption="All Guests"
            renderItem={(arr) => (
              <div data-testid="hotel-all-guests-list" style={{ fontSize: '12px', lineHeight: 1.5 }}>
                {arr.map((g, i) => (
                  <div key={i} data-testid={`hotel-guest-${i}`}>• {g}</div>
                ))}
              </div>
            )}
            emptyHint="—"
            testId="hotel-all-guests"
          />
        </CompactCard>
      </div>
      {paxSummary && (
        <div style={{ fontSize: '12px', color: '#4b5563', marginBottom: '12px' }} data-testid="hotel-pax-summary">
          Total: {paxSummary}
        </div>
      )}

      {/* ---------- Section 4: Extra Bed ---------- */}
      {(showExtraBed || editable) && (
        <div
          data-testid="hotel-extra-bed-card"
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            padding: '8px 12px',
            borderRadius: '4px',
            marginBottom: '12px',
            fontSize: '12px',
            color: '#92400e',
          }}
        >
          <SectionCaption color="#92400e">
            Extra Bed
            {extraBedConfirmed && (
              <span
                data-testid="hotel-extra-bed-confirmed-pill"
                style={{
                  marginLeft: '8px',
                  padding: '1px 8px',
                  fontSize: '9px',
                  background: '#d1fae5',
                  border: '1px solid #6ee7b7',
                  color: '#065f46',
                  borderRadius: '10px',
                  letterSpacing: '0.06em',
                }}
              >
                CONFIRMED
              </span>
            )}
          </SectionCaption>
          {editable && !locked ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                data-testid="hotel-extra-bed-toggle-label"
              >
                <input
                  type="checkbox"
                  checked={extraBed}
                  data-testid="hotel-extra-bed-toggle"
                  onChange={(e) => onFieldSave?.('extraBed', e.target.checked)}
                />
                Extra bed requested
              </label>
              {extraBed && (
                <>
                  <label
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                    data-testid="hotel-extra-bed-confirmed-toggle-label"
                  >
                    <input
                      type="checkbox"
                      checked={extraBedConfirmed}
                      data-testid="hotel-extra-bed-confirmed-toggle"
                      onChange={(e) => onFieldSave?.('extraBedConfirmed', e.target.checked)}
                    />
                    Extra bed confirmed by hotel
                  </label>
                  <label
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                    data-testid="hotel-extra-bed-payable-toggle-label"
                  >
                    <input
                      type="checkbox"
                      checked={extraBedPayableAtHotel}
                      data-testid="hotel-extra-bed-payable-toggle"
                      onChange={(e) => onFieldSave?.('extraBedPayableAtHotel', e.target.checked)}
                    />
                    Payment to be made directly at the hotel
                  </label>
                </>
              )}
            </div>
          ) : (
            <div>
              {extraBedConfirmed
                ? '1 extra bed confirmed by the hotel'
                : '1 extra bed arranged · Subject to hotel confirmation'}
              {extraBedPayableAtHotel && (
                <div data-testid="hotel-extra-bed-payable-line">
                  Payment for the extra bed to be made directly at the hotel.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---------- Section 5: Transfers ---------- */}
      {(transferDetails.length > 0 || editable) && (
        <CompactCard style={{ marginBottom: '12px' }}>
          <CompactEditableArray
            items={transferDetails}
            editable={editable}
            locked={locked}
            fieldKey="transferDetails"
            onFieldSave={onFieldSave}
            caption="Transfers Included"
            renderItem={(arr) => (
              <div data-testid="hotel-transfers-list" style={{ fontSize: '12px', lineHeight: 1.5 }}>
                {arr.map((t, i) => (
                  <div key={i}>• {t}</div>
                ))}
              </div>
            )}
            testId="hotel-transfers"
          />
        </CompactCard>
      )}

      {/* ---------- Section 6: Inclusions ---------- */}
      {(inclusions.length > 0 || editable || mealPlan) && (
        <CompactCard style={{ marginBottom: '12px' }}>
          <CompactEditableArray
            items={inclusions}
            editable={editable}
            locked={locked}
            fieldKey="hotelInclusions"
            onFieldSave={onFieldSave}
            caption="Inclusions"
            renderItem={(arr) => {
              // Auto-prepend the chosen meal plan as a synthetic first bullet.
              // NOT persisted — stays in sync if mealPlan is edited later.
              // User-added items (discount coupons, spa, etc.) follow below.
              const expanded = expandMealPlan(mealPlan);
              const merged = expanded ? [`Meal Plan: ${expanded}`, ...arr] : arr;
              const useTwoCol = merged.length > 4;
              return (
                <div
                  data-testid="hotel-inclusions-list"
                  style={{
                    columnCount: useTwoCol ? 2 : 1,
                    columnGap: '16px',
                    fontSize: '12px',
                    lineHeight: 1.5,
                  }}
                >
                  {merged.map((it, i) => (
                    <div
                      key={i}
                      style={{ breakInside: 'avoid', marginBottom: '2px' }}
                      data-testid={i === 0 && expanded ? 'hotel-inclusion-meal-plan' : `hotel-inclusion-${i}`}
                    >
                      • {it}
                    </div>
                  ))}
                </div>
              );
            }}
            testId="hotel-inclusions"
          />
        </CompactCard>
      )}

      {/* ---------- Section 7: Amenities (bullet list per refinement spec) ---------- */}
      {(amenities.length > 0 || editable) && (
        <CompactCard style={{ marginBottom: '12px' }}>
          <CompactEditableArray
            items={amenities}
            editable={editable}
            locked={locked}
            fieldKey="amenities"
            onFieldSave={onFieldSave}
            caption="Amenities"
            renderItem={(arr) => (
              <div
                data-testid="hotel-amenities-list"
                style={{
                  columnCount: arr.length > 4 ? 2 : 1,
                  columnGap: '16px',
                  fontSize: '12px',
                  lineHeight: 1.5,
                }}
              >
                {arr.map((a, i) => (
                  <div key={i} style={{ breakInside: 'avoid', marginBottom: '2px' }}>
                    • {a}
                  </div>
                ))}
              </div>
            )}
            testId="hotel-amenities"
          />
        </CompactCard>
      )}

      {/* ---------- Section 8: Cancellation (gated by showCancellation) ---------- */}
      {showCancellation && (cancellationPolicy.length > 0 || editable) && (
        <div
          data-testid="hotel-cancellation-card"
          style={{
            border: '1px solid #fcd34d',
            background: '#fffbeb',
            borderRadius: '6px',
            padding: '12px 16px',
            marginBottom: '12px',
            pageBreakInside: 'avoid',
            breakInside: 'avoid',
          }}
        >
          <CompactEditableArray
            items={cancellationPolicy}
            editable={editable}
            locked={locked}
            fieldKey="hotelCancellationPolicy"
            onFieldSave={onFieldSave}
            caption="⚠ Cancellation Policy"
            captionColor="#92400e"
            renderItem={(arr) => (
              <div>
                {arr.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: '12px',
                      color: '#78350f',
                      marginBottom: '4px',
                      lineHeight: 1.5,
                    }}
                  >
                    • {p}
                  </div>
                ))}
              </div>
            )}
            testId="hotel-cancellation"
          />
        </div>
      )}

      {/* ---------- Section 9: Check-in/out time note ---------- */}
      {(checkInTime || checkOutTime) && (
        <div
          data-testid="hotel-checkin-time-note"
          style={{
            fontSize: '12px',
            color: '#6b7280',
            padding: '8px 12px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '4px',
            marginBottom: '8px',
          }}
        >
          🕐 Check-in from {checkInTime || '14:00'} · Check-out by {checkOutTime || '12:00'}
        </div>
      )}

      {/* ---------- Section 10: Important Notes (default template fallback) ---------- */}
      {importantNotes.length > 0 && (
        <CompactCard style={{ marginBottom: '0' }}>
          <CompactEditableArray
            items={importantNotes}
            editable={editable}
            locked={locked}
            fieldKey="importantNotes"
            onFieldSave={onFieldSave}
            caption="Important Notes"
            defaultTemplate={DEFAULT_HOTEL_IMPORTANT_NOTES}
            renderItem={(arr) => (
              <div data-testid="hotel-important-notes-list" style={{ fontSize: '12px', lineHeight: 1.55 }}>
                {arr.map((n, i) => (
                  <div key={i} style={{ marginBottom: '2px' }}>• {n}</div>
                ))}
              </div>
            )}
            testId="hotel-important-notes"
          />
        </CompactCard>
      )}

      {/* Section 11 — NO barcode / QR is intentionally absent. */}
    </div>
  );
}
